// Per-client Schedule — the recurring patterns that generate this client's
// visits, what they produce over the next four weeks, and any open hours
// offered to a contracted agency. Editing happens on Schedule (CAERA); this
// is the client-side view of it.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../../lib/supabase';
import AgencyCarePlanCard from './AgencyCarePlanCard';
import { LIVE_OFFER_STATUSES, offerSummary, offerStatusText } from '../../lib/agencyOffers';

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '6px 8px', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, verticalAlign: 'top' };
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS = { active: { label: 'Active', color: 'var(--success)' }, paused: { label: 'Paused', color: 'var(--warn)' }, ended: { label: 'Ended', color: 'var(--text3)' } };
const hhmm = (t) => (t ? String(t).slice(0, 5) : '—');
const dayList = (arr) => (arr || []).map(d => DAYS[d]).join(' ');

export default function ClientScheduleTab({ client }) {
  const [schedules, setSchedules] = useState([]);
  const [visits, setVisits] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const in28 = new Date(Date.now() + 28 * 86400000).toISOString();
    const [s, v, o] = await Promise.all([
      supabase.from('schedules')
        .select('id, days_of_week, start_time, end_time, frequency_weeks, start_date, end_date, status, source, hours_per_visit, notes, staff:staff ( id, first_name, last_name, position_code ), service:service_catalog ( id, name, code )')
        .eq('client_id', client.id).order('status').order('start_date', { ascending: false }),
      supabase.from('visits')
        .select('id, evv_visit_id, scheduled_start, scheduled_end, status, timezone, schedule_id, staff:staff ( first_name, last_name ), service:service_catalog ( name, code, billable )')
        .eq('client_id', client.id).gte('scheduled_start', new Date().toISOString()).lte('scheduled_start', in28)
        .order('scheduled_start').limit(60),
      supabase.from('client_agency_offers')
        .select('id, service_type, role_needed, hours_needed, start_date, status, expires_at, responded_by_name, response_note, created_at, agency:contracting_agencies ( name )')
        .eq('client_id', client.id).in('status', LIVE_OFFER_STATUSES),
    ]);
    setSchedules(s.data || []); setVisits(v.data || []); setOffers(o.data || []);
    setLoading(false);
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const active = schedules.filter(x => x.status === 'active');
    return {
      active: active.length,
      paused: schedules.filter(x => x.status === 'paused').length,
      weekly: active.reduce((sum, x) => sum + (Number(x.hours_per_visit) || 0) * (x.days_of_week || []).length / (x.frequency_weeks || 1), 0),
      upcoming: visits.length,
      unassigned: visits.filter(x => !x.staff).length,
    };
  }, [schedules, visits]);

  if (loading) return <p style={{ color: 'var(--text3)' }}>Loading…</p>;

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Schedule</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to={`/hh/calendar?client=${client.id}`} className="btn btn-outline btn-sm">Open calendar</Link>
            <Link to="/hh/scheduling" className="btn btn-outline btn-sm">Schedule (CAERA)</Link>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text2)' }}>
          <span><b>{totals.active}</b> active pattern{totals.active === 1 ? '' : 's'}</span>
          {totals.paused > 0 && <span><b>{totals.paused}</b> paused</span>}
          <span><b>{totals.weekly.toFixed(1)}h</b> scheduled per week</span>
          <span><b>{totals.upcoming}</b> visits in the next 4 weeks</span>
          {totals.unassigned > 0 && <span style={{ color: 'var(--danger)' }}><b>{totals.unassigned}</b> unassigned</span>}
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Recurring patterns</h3>
        {schedules.length === 0 ? (
          <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: 0 }}>
            No CAERA patterns for this client. While the service line still takes its visits from AxisCare, the calendar shows AxisCare's schedule instead.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Service', 'Days', 'Time', 'Every', 'Caregiver', 'From', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {schedules.map(s => {
                  const st = STATUS[s.status] || STATUS.ended;
                  return (
                    <tr key={s.id}>
                      <td style={td}>{s.service?.name || '—'}<div style={{ fontSize: 10.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{s.service?.code}</div></td>
                      <td style={td}>{dayList(s.days_of_week)}</td>
                      <td style={td}>{hhmm(s.start_time)}–{hhmm(s.end_time)}<div style={{ fontSize: 10.5, color: 'var(--text3)' }}>{s.hours_per_visit}h</div></td>
                      <td style={td}>{s.frequency_weeks === 1 ? 'week' : `${s.frequency_weeks} weeks`}</td>
                      <td style={td}>{s.staff ? `${s.staff.last_name}, ${s.staff.first_name}` : <span style={{ color: 'var(--danger)' }}>Open</span>}</td>
                      <td style={td}>{s.start_date ? format(parseISO(s.start_date), 'd MMM yy') : '—'}{s.end_date ? ` – ${format(parseISO(s.end_date), 'd MMM yy')}` : ''}</td>
                      <td style={{ ...td, color: st.color, fontWeight: 600 }}>{st.label}{s.source === 'axiscare' ? <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>from AxisCare</div> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Next four weeks</h3>
        {visits.length === 0 ? <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: 0 }}>Nothing scheduled.</p> : (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['When', 'Service', 'Caregiver', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {visits.map(v => (
                  <tr key={v.id}>
                    <td style={td}>{format(parseISO(v.scheduled_start), 'EEE d MMM · h:mmaaa')} – {format(parseISO(v.scheduled_end), 'h:mmaaa')}</td>
                    <td style={td}>{v.service?.name}{v.service?.billable === false ? <span style={{ fontSize: 10, color: 'var(--text3)' }}> · non-billable</span> : null}</td>
                    <td style={td}>{v.staff ? `${v.staff.last_name}, ${v.staff.first_name}` : <span style={{ color: 'var(--danger)' }}>Unassigned</span>}</td>
                    <td style={td}>{v.status.replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {offers.length > 0 && (
        <div style={card}>
          <h3 style={{ marginTop: 0, fontSize: 14 }}>Offered to agencies</h3>
          {offers.map(o => (
            <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid var(--border)', fontSize: 12.5 }}>
              <span>{o.agency?.name} · {offerSummary(o)}</span>
              <span style={{ color: 'var(--text3)' }}>{offerStatusText(o)} · offered {format(parseISO(o.created_at), 'd MMM')}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>Managed on Schedule (CAERA) → Agency Scheduling.</div>
        </div>
      )}

      <AgencyCarePlanCard client={client} onOffersChanged={load} />
    </div>
  );
}
